import {
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_ENTITY_SEED,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_TEMPORAL_MONTHLY,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import React, { useMemo } from 'react';
import type {
  SummaryDataMetric,
  SummaryEntityCategory,
} from '../../../../../redux/slices/InsightsSlice';
import { computeSummary } from '../computeSummary';
import { resolveTotalColumnLabel, scaleForDisplay } from '../insightUtils';
import SummaryGrid, { type SummaryGridRow } from '../SummaryGrid';
import CopyButton from './CopyButton';
import {
  pairRowsBySide,
  SUMMARY_DATA_SIDE_CATEGORIES,
  type SummaryDataSide,
} from './dataBasis';
import { formatNumber } from './formatNumber';
import SectionCard from './SectionCard';
import { buildTsv } from './summary2Utils';

const MONTH_HEADERS = [
  'No date',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthlySectionTableProps {
  category: SummaryEntityCategory;
  title: string;
  metric: SummaryDataMetric;
  metricLabel: string;
  inputs: IInput[];
  gUnits: GUnit[];
  /** Plan, Actual, or both — both renders paired Plan / Actual sub-columns, never a summed value. */
  sides: SummaryDataSide[];
}

/** Row identity across sides — a product's unit is part of it, so kg and L rows never share columns. */
const gridRowKey = (row: SummaryGridRow): string =>
  [row.group ?? '', row.label, row.unit ?? ''].join('|');

/**
 * Merge each side's rows into one row per item with the sides interleaved
 * column by column ([Total·Plan, Total·Actual, No date·Plan, …]) — the
 * layout SummaryGrid's `sideLabels` mode reads.
 */
const interleaveSides = (
  rowsBySide: Partial<Record<SummaryDataSide, SummaryGridRow[]>>,
  sides: SummaryDataSide[]
): SummaryGridRow[] =>
  pairRowsBySide(rowsBySide, gridRowKey)
    .map(({ sides: bySide }) => {
      const first = Object.values(bySide)[0]!;
      const columnCount = Math.max(
        ...sides.map((side) => bySide[side]?.values.length ?? 0)
      );
      const values: number[] = [];
      for (let ci = 0; ci < columnCount; ci++) {
        for (const side of sides) values.push(bySide[side]?.values[ci] ?? 0);
      }
      const mergeCount = Math.max(
        ...sides.map((side) => bySide[side]?.mergeCount ?? 0)
      );
      const row: SummaryGridRow = { label: first.label, values };
      if (first.group !== undefined) row.group = first.group;
      if (first.unit !== undefined) row.unit = first.unit;
      if (mergeCount > 1) row.mergeCount = mergeCount;
      return row;
    })
    .sort(
      (a, b) =>
        (a.group ?? '').localeCompare(b.group ?? '', undefined, {
          sensitivity: 'base',
        }) || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );

/**
 * Monthly-breakdown rendering of one section, driven by the page-level
 * Total/Monthly control and Metric selector (both shared across every
 * section — see SummaryReportView). Reuses today's original Summary lens's
 * own `computeSummary` + `SummaryGrid`, both unmodified, scoped to a single
 * category — this is just those pieces re-parented under Summary 2 instead
 * of a page-level dropdown. Each side is computed separately (one task
 * category each), so Plan and Actual are never added together.
 */
const MonthlySectionTable: React.FC<MonthlySectionTableProps> = ({
  category,
  title,
  metric,
  metricLabel,
  inputs,
  gUnits,
  sides,
}) => {
  const isPaired = sides.length > 1;
  const sideLabels = useMemo(
    () => sides.map((side) => SUMMARY_DATA_BASIS_LABELS[side]),
    [sides]
  );

  const rows = useMemo(() => {
    const rowsBySide: Partial<Record<SummaryDataSide, SummaryGridRow[]>> = {};
    for (const side of sides) {
      rowsBySide[side] = computeSummary(
        inputs,
        category,
        metric,
        SUMMARY_TEMPORAL_MONTHLY,
        gUnits,
        SUMMARY_DATA_SIDE_CATEGORIES[side]
      );
    }
    return isPaired
      ? interleaveSides(rowsBySide, sides)
      : (rowsBySide[sides[0]] ?? []);
  }, [inputs, category, metric, gUnits, sides, isPaired]);

  const hasGroup = category === SUMMARY_ENTITY_SEED;

  const { copied, copy } = useCopyToClipboard(() => {
    const isQuantityMetric = metric === SUMMARY_METRIC_QUANTITY;
    const totalColumnLabel = resolveTotalColumnLabel(metric, metricLabel);
    const columnHeaders = [totalColumnLabel, ...MONTH_HEADERS];
    const dataHeaders = isPaired
      ? columnHeaders.flatMap((col) =>
          sideLabels.map((side) => `${col} (${side})`)
        )
      : columnHeaders;
    const valueHeaders = [...dataHeaders];
    if (isQuantityMetric) valueHeaders.splice(sides.length, 0, 'Unit');
    const headerCols = hasGroup
      ? ['Crop', 'Variety', ...valueHeaders]
      : ['Item', ...valueHeaders];

    const body = rows.map((row) => {
      const rowScale = scaleForDisplay(
        Math.max(...sides.map((_, ci) => row.values[ci] ?? 0)),
        row.unit
      );
      const rowDivisor = rowScale.unit === 't' ? 1000 : 1;
      const valueCols = dataHeaders.map((_, ci) =>
        formatNumber((row.values[ci] ?? 0) / rowDivisor)
      );
      if (isQuantityMetric)
        valueCols.splice(sides.length, 0, rowScale.unit ?? '');
      const entityCols = hasGroup ? [row.group ?? '', row.label] : [row.label];
      return [...entityCols, ...valueCols];
    });

    return buildTsv(headerCols, body);
  });

  return (
    <SectionCard
      id={`summary2-${category}`}
      title={`${title} — Monthly`}
      actions={rows.length > 0 && <CopyButton copied={copied} onCopy={copy} />}
    >
      <SummaryGrid
        entityCategory={category}
        dataMetric={metric}
        temporalLayout={SUMMARY_TEMPORAL_MONTHLY}
        rows={rows}
        metricLabel={metricLabel}
        sideLabels={isPaired ? sideLabels : undefined}
      />
    </SectionCard>
  );
};

export default MonthlySectionTable;
